import React, { useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { TablePagination } from "../../leave-management/components/TablePagination";
import { ui } from "../../../components/ui";
import { cn } from "../../../lib/utils";
import {
  allocationStatusClass,
  allocationStatusLabel,
} from "../utils/allocationStatus";

const MONTH_KEYS = [
  "dateTime.monthJan",
  "dateTime.monthFeb",
  "dateTime.monthMar",
  "dateTime.monthApr",
  "dateTime.monthMay",
  "dateTime.monthJun",
  "dateTime.monthJul",
  "dateTime.monthAug",
  "dateTime.monthSep",
  "dateTime.monthOct",
  "dateTime.monthNov",
  "dateTime.monthDec",
];

function MetricRow({ label, value }) {
  return (
    <div className="flex justify-between gap-3 text-xs">
      <span className="per-file-hint shrink-0">{label}</span>
      <span className="font-semibold text-foreground text-right truncate">{value}</span>
    </div>
  );
}

export function CustomerAllocationTable({
  data,
  page,
  pageSize,
  onPageChange,
  totalItems,
}) {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const resolvedTotalItems = totalItems ?? data.length;
  const totalPages = Math.max(1, Math.ceil(resolvedTotalItems / pageSize));
  const pagedRows = useMemo(() => {
    if (totalItems != null) return data;
    const start = (page - 1) * pageSize;
    return data.slice(start, start + pageSize);
  }, [data, page, pageSize, totalItems]);

  const formatDate = (isoStr) => {
    if (!isoStr) return "—";
    const dateObj = new Date(isoStr);
    if (!Number.isFinite(dateObj.getTime())) return "—";
    const day = String(dateObj.getDate()).padStart(2, "0");
    const month = t(MONTH_KEYS[dateObj.getMonth()]);
    const year = dateObj.getFullYear();
    return `${day} ${month} ${year}`;
  };

  const viewPath = (row) =>
    `/inventory-management/allocation-detail/${row.allocationId}`;

  const handleRowClick = (row) => {
    if (!row?.allocationId) return;
    navigate(viewPath(row));
  };

  const StatusBadge = ({ status }) => (
    <span
      className={cn(
        "inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-bold border",
        allocationStatusClass(status)
      )}
    >
      {allocationStatusLabel(status, t)}
    </span>
  );

  /** Prefer backend lineStatuses (distinct) when present on the search row. */
  const StatusCell = ({ row }) => {
    const fromMapped = Array.isArray(row.lineStatuses) ? row.lineStatuses : [];
    const fromRaw = Array.isArray(row.raw?.lineStatuses)
      ? row.raw.lineStatuses
      : Array.isArray(row.raw?.line_statuses)
        ? row.raw.line_statuses
        : [];
    const statuses =
      fromMapped.length > 0
        ? fromMapped
        : fromRaw.length > 0
          ? fromRaw.map((s) => String(s ?? "").trim()).filter(Boolean)
          : row.status
            ? [row.status]
            : [];
    if (statuses.length === 0) return <span className="per-file-hint">—</span>;
    return (
      <div className="flex flex-wrap items-center gap-1.5 max-w-[220px]">
        {statuses.map((status, idx) => (
          <StatusBadge key={`${String(status)}-${idx}`} status={status} />
        ))}
      </div>
    );
  };

  return (
    <div className="font-sans">
      {/* Desktop table */}
      <div className="hidden md:block overflow-x-auto">
        <table className="w-full text-sm min-w-[1200px]">
          <thead>
            <tr className={ui.tableHead}>
              <th className="px-4 py-3 font-semibold whitespace-nowrap text-left">
                {t("inventory.reference")}
              </th>
              <th className="px-4 py-3 font-semibold whitespace-nowrap text-left">
                {t("dateTime.date")}
              </th>
              <th className="px-4 py-3 font-semibold whitespace-nowrap text-left">
                {t("inventory.customerName")}
              </th>
              <th className="px-4 py-3 font-semibold whitespace-nowrap text-left">
                {t("inventory.iofNumber")}
              </th>
             
              <th className="px-4 py-3 font-semibold whitespace-nowrap text-left">
                {t("inventory.salesAndService")}
              </th>
              <th className="px-4 py-3 font-semibold whitespace-nowrap text-left">
                {t("inventory.dispatchMethod")}
              </th>
              <th className="px-4 py-3 font-semibold whitespace-nowrap text-left">
                {t("dateTime.finalDeliveryDate")}
              </th>
              <th className="px-4 py-3 font-semibold whitespace-nowrap text-left">
                {t("status.status")}
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-(--color-text-border)/35">
            {pagedRows.map((row) => (
              <tr
                key={row.id || row.allocationId}
                onClick={() => handleRowClick(row)}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    handleRowClick(row);
                  }
                }}
                title={t("inventory.allocationDetail")}
                className="hover:bg-workspace/20 transition-colors cursor-pointer"
              >
                <td className="px-4 py-3.5 font-semibold text-foreground font-mono whitespace-nowrap">
                  {row.reference || "—"}
                </td>
                <td className="px-4 py-3.5 per-file-hint whitespace-nowrap">
                  {formatDate(row.date)}
                </td>
                <td className="px-4 py-3.5">
                  <span className="font-semibold text-foreground block truncate leading-tight">
                    {row.customerName || "—"}
                  </span>
                </td>
                <td className="px-4 py-3.5 per-file-hint font-mono truncate">
                  {row.iofNumber || "—"}
                </td>
                
                <td className="px-4 py-3.5 per-file-hint whitespace-nowrap">
                  {row.orderType === "Service"
                    ? t("inventory.service")
                    : t("inventory.sales")}
                </td>
                <td className="px-4 py-3.5 per-file-hint whitespace-nowrap">
                  {row.dispatchMethod || "—"}
                </td>
                <td className="px-4 py-3.5 per-file-hint whitespace-nowrap">
                  {formatDate(row.deliveryDate)}
                </td>
                <td className="px-4 py-3.5">
                  <StatusCell row={row} />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Mobile cards */}
      <ul className="md:hidden space-y-2.5 p-3">
        {pagedRows.map((row) => (
          <li
            key={row.id || row.allocationId}
            onClick={() => handleRowClick(row)}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                handleRowClick(row);
              }
            }}
            className="rounded-xl border border-(--color-text-border)/60 bg-white px-3.5 py-3 space-y-2.5 shadow-xs active:bg-workspace/40 cursor-pointer"
          >
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <p className="text-sm font-semibold text-foreground truncate">
                  {row.customerName || "—"}
                </p>
                <p className="text-xs font-mono per-file-hint mt-0.5">
                  {row.reference || "—"}
                </p>
              </div>
              <div className="shrink-0">
                <StatusCell row={row} />
              </div>
            </div>
            <div className="space-y-1.5 pt-1 border-t border-(--color-text-border)/25">
              <MetricRow label={t("dateTime.date")} value={formatDate(row.date)} />
              <MetricRow label={t("inventory.iofNumber")} value={row.iofNumber || "—"} />
              
              <MetricRow
                label={t("inventory.salesAndService")}
                value={
                  row.orderType === "Service"
                    ? t("inventory.service")
                    : t("inventory.sales")
                }
              />
              <MetricRow
                label={t("inventory.dispatchMethod")}
                value={row.dispatchMethod || "—"}
              />
              <MetricRow
                label={t("dateTime.finalDeliveryDate")}
                value={formatDate(row.deliveryDate)}
              />
            </div>
          </li>
        ))}
      </ul>

      <div className="px-4 py-3">
        <TablePagination
          page={page}
          totalPages={totalPages}
          totalItems={resolvedTotalItems}
          pageSize={pageSize}
          onPageChange={onPageChange}
        />
      </div>
    </div>
  );
}
